import React, { useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { ErrorBoundary, ToastContainer } from '@/components/common';
import { AutoLogoutTest } from '@/components/AutoLogoutTest';
import { AppProviders } from '@/store';
import { useReduxAuth } from '@/hooks/useReduxAuth';
import { useStorageExpiry } from '@/hooks/useStorageExpiry';
import { useAutoLogout } from '@/hooks/useAutoLogout';
import { storage } from '@/utils/storage';
import { SuperadminAuthProvider } from '@/superadmin/contexts/SuperadminAuthContext';
import { theme, GlobalStyles } from '@/styles';
import { router } from '@/routes/AppRouter';
import { setupInterceptors } from '@/api';

// App component without providers
const AppContent: React.FC = () => {
  const { restoreAuth, loading } = useReduxAuth();
  useStorageExpiry(); // Auto-cleanup expired storage
  useAutoLogout(); // Direct 2-minute auto logout

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("login_as") === "true") {
      const merchantData = storage.getSession("merchant_auth_user");
      if (merchantData) {
        dispatch(setMerchantAuth(merchantData));
      }
    }
  }, []);


  useEffect(() => {
    // Setup API interceptors
    setupInterceptors();

    // Restore authentication state from Redux
    restoreAuth().finally(() => {});
  }, [restoreAuth]);

  // Show loading spinner while auth is being restored
  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            backgroundColor: theme.palette.background.default,
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginTop: '8px', fontSize: '20px' }}>Loading GrowthBnk...</div>
            <div style={{ marginTop: '8px', fontSize: '14px', opacity: 0.7 }}>
              Your account is getting created...
            </div>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles
        styles={theme => ({
          '*': {
            margin: 0,
            padding: 0,
            boxSizing: 'border-box',
          },
          html: {
            height: '100%',
            WebkitOverflowScrolling: 'touch',
          },
          body: {
            height: '100%',
            fontFamily: theme.typography.fontFamily,
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            lineHeight: 1.5,
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
          '#root': {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
        })}
      />
      <ErrorBoundary>
        <AutoLogoutTest />
        <RouterProvider router={router} />
        <ToastContainer />
      </ErrorBoundary>
    </ThemeProvider>
  );
};

// Main App component with providers
const App: React.FC = () => {
  return (
    <AppProviders>
      <SuperadminAuthProvider>
        <AppContent />
      </SuperadminAuthProvider>
    </AppProviders>
  );
};

export default App;
